import { PaginationTable } from "js/components/types";

import { AxiosInstance } from "../utils/AxiosInstance";

export const createContract = async (contractData: any) => {
  const { data } = await AxiosInstance.post(`/v1/contracts/`, contractData);
  return data;
};

export const previewContract = async (contractId: string) => {
  const { data } = await AxiosInstance.get(
    `/v1/contracts/${contractId}/preview`,
    { responseType: "blob" },
  );
  return data;
};

export const downloadWordDocumentContract = async (
  contractId: string,
  downloadAlternativeJustice: boolean = false,
  downloadSecureRentPolicy: boolean = false,
  includeWatermark: boolean = true,
  forceDocxFallback: boolean = false,
) => {
  const queryParams = new URLSearchParams();

  if (downloadAlternativeJustice) {
    queryParams.append("download_alternative_justice", "true");
  }

  if (downloadSecureRentPolicy) {
    queryParams.append("download_secure_rent_policy", "true");
  }

  queryParams.append("include_watermark", includeWatermark ? "true" : "false");

  if (forceDocxFallback) {
    queryParams.append("force_docx_fallback", "true");
  }

  const queryString = queryParams.toString();
  const url = `/v1/contracts/${contractId}/download/word-document${queryString ? `?${queryString}` : ""}`;

  const response = await AxiosInstance.get(url, { responseType: "blob" });
  return response;
};

export const updateContract = async (contractId: string, contractData: any) => {
  const { data } = await AxiosInstance.put(
    `/v1/contracts/${contractId}`,
    contractData,
  );
  return data;
};

export const confirmContract = async (contractId: any) => {
  const { data } = await AxiosInstance.put(
    `/v1/contracts/${contractId}/confirm`,
  );
  return data;
};

export const retrieveContract = async (contractId: any) => {
  const { data } = await AxiosInstance.get(`/v1/contracts/${contractId}`);
  return data;
};

export const retrieveContractsList = async (
  pagination: PaginationTable,
  searchParams?: {
    search?: string;
    status?: string;
    is_active?: string;
  },
) => {
  const params: any = {
    limit: pagination.pageSize,
    offset: (pagination.current - 1) * pagination.pageSize,
  };

  if (searchParams?.search) {
    params.search = searchParams.search;
  }

  if (searchParams?.status) {
    params.status = searchParams.status;
  }

  if (searchParams?.is_active) {
    params.is_active = searchParams.is_active;
  }

  const { data } = await AxiosInstance.get(`/v1/contracts/`, {
    params,
  });
  return data;
};

export const createStandaloneContract = async (contractData: any) => {
  const { data } = await AxiosInstance.post(`/v1/contracts/`, {
    ...contractData,
    create_standalone: true,
  });
  return data;
};

export const getContractDetails = async (contractId: string) => {
  const { data } = await AxiosInstance.get(
    `/v1/contracts/${contractId}/details`,
  );
  return data;
};

/**
 * Get base clauses with filters
 */
export interface ClauseFilters {
  applicable_housing?: boolean;
  applicable_commercial?: boolean;
  applicable_industrial?: boolean;
  applicable_with_guarantor?: boolean;
  applicable_state?: string;
  category_id?: string;
  is_active?: boolean;
  search?: string;
}

export const getBaseClauses = async (filters?: ClauseFilters) => {
  const params: any = {};

  if (filters) {
    if (filters.applicable_housing !== undefined) {
      params.applicable_housing = filters.applicable_housing;
    }
    if (filters.applicable_commercial !== undefined) {
      params.applicable_commercial = filters.applicable_commercial;
    }
    if (filters.applicable_industrial !== undefined) {
      params.applicable_industrial = filters.applicable_industrial;
    }
    if (filters.applicable_with_guarantor !== undefined) {
      params.applicable_with_guarantor = filters.applicable_with_guarantor;
    }
    if (filters.applicable_state) {
      params.applicable_state = filters.applicable_state;
    }
    if (filters.category_id) {
      params.category_id = filters.category_id;
    }
    if (filters.is_active !== undefined) {
      params.is_active = filters.is_active;
    }
    if (filters.search) {
      params.search = filters.search;
    }
  }

  const { data } = await AxiosInstance.get(`/v1/contracts/base-clauses/`, {
    params,
  });
  return data;
};

// ── Renovaciones: semilla de clausulado (MB-379) ─────────────────────
//
// El folio de una renovación es un folio NUEVO. Todo lo que el asesor ve en la carátula se
// hidrata de cero desde el multiexpediente, pero el clausulado que las dos partes negociaron el
// año pasado no está en ninguna parte del expediente: vive en las cláusulas del contrato
// anterior, y este endpoint es el único que sabe recuperarlo.
//
// El tipado NO reusa la forma del editor a propósito: estas interfaces describen lo que manda el
// SERVIDOR, con sus nulos y sus campos ausentes, y `js/utils/renewalClauseSeed.ts` es el único
// sitio donde eso se traduce a la forma del editor.

/** Una variable de cláusula tal como la guarda `Clause.variables` (JSONField, sin validación). */
export interface RenewalSeedClauseVariable {
  code?: string | null;
  name?: string | null;
  /** Cualquier cadena: el JSONField no lo valida y `create_from_base_clause` ni lo copia. */
  input_type?: string | null;
  required?: boolean | null;
  value?: string | null;
}

export interface RenewalSeedContentBlock {
  id?: string | null;
  html?: string | null;
  conditionKey?: string | null;
  conditionValue?: boolean | null;
}

export interface RenewalSeedClause {
  id?: string | null;
  number?: number | null;
  title?: string | null;
  blocks?: RenewalSeedContentBlock[] | null;
  baseClauseId?: string | null;
  variables?: RenewalSeedClauseVariable[] | null;
  conditionKey?: string | null;
  conditionValue?: boolean | null;
}

/**
 * Una cláusula que NO se copió del contrato anterior, sino que se volvió a derivar de la
 * plantilla vigente porque su texto lleva dentro las fechas del arrendamiento.
 *
 * Es un cambio real sobre lo pactado: `BaseClause` es mutable y sin versionar, así que la
 * plantilla de hoy puede no decir lo que decía el día que se firmó.
 */
export interface RenewalSeedRegeneratedClause {
  title: string;
  base_clause_id: string;
  previous_title: string;
  /** La clave de la cláusula en `clauses_by_id`: su id en el documento sembrado. */
  clause_id?: string | null;
  /**
   * La cláusula en texto PLANO tal como salió en el PDF firmado, para "Comparar con la firmada".
   * `null` si no apareció en el documento firmado o no se pudo reconstruir.
   */
  previous_content?: string | null;
}

export interface RenewalClauseSeed {
  /** `false` para un folio normal: la señal de seguir sembrando la plantilla. */
  is_renewal: boolean;
  previous_folio?: string;
  previous_document_id?: string | null;
  /** Mismo contenido que `editor_data.clausesById`, con la forma del servidor. */
  clauses_by_id?: Record<string, RenewalSeedClause> | null;
  /** El orden. Puede venir vacío aun con `is_renewal: true` (no se localizó el contrato previo). */
  clause_ids?: string[] | null;
  regenerated?: RenewalSeedRegeneratedClause[] | null;
  /**
   * Las declaraciones que el contrato anterior tenía distintas de la plantilla (MB-380). NO se
   * copian —son hechos que se protestan de nuevo—, solo se avisa. `unknown` a propósito: el único
   * que lo lee es `parseDeclarationsReview` (`js/utils/renewalClauseSeed.ts`), que fija la forma.
   */
  declarations_review?: unknown;
}

/**
 * El clausulado con el que debe nacer el contrato de un folio.
 *
 * Contesta 200 también para un folio normal (`is_renewal: false`), así que la carga del editor lo
 * pide SIEMPRE y no necesita saber de antemano qué folio es una renovación. Puede contestar 400
 * con `{ message, code: "INCOMPLETE_REQUIREMENTS" }` cuando al expediente le faltan los datos
 * para interpolar las fechas dentro del texto.
 */
export const getRenewalClauseSeed = async (
  folioNumber: string,
): Promise<RenewalClauseSeed> => {
  const { data } = await AxiosInstance.get(
    `/v1/contracts/${folioNumber}/renewal-clause-seed`,
  );
  return data;
};

// ── Renovaciones: confirmación de la revisión (MB-380) ───────────────
//
// Antes de descargar una renovación, un abogado confirma los puntos que el clausulado heredado no
// pudo copiar tal cual. El servidor guarda quién y cuándo en `metadata.renewal_review` (y una fila
// `VERIFIED` en el historial). La respuesta es `unknown` a propósito: el único que la lee es
// `parseRenewalReviewConfirmation` (`js/utils/renewalReviewConfirmation.ts`).

/** Un punto tal como viaja al servidor y como vuelve guardado. */
export interface RenewalReviewConfirmationItem {
  kind: "clause" | "declaration";
  key: string;
  /** El título que vio el abogado: lo que se lee en el historial. */
  label: string;
}

export const confirmRenewalReview = async (
  contractId: string,
  items: RenewalReviewConfirmationItem[],
): Promise<{ renewal_review?: unknown }> => {
  const { data } = await AxiosInstance.post(
    `/v1/contracts/${contractId}/renewal-review`,
    { items },
  );
  return data;
};

export const getContractHistory = async (contractId: string) => {
  const response = await AxiosInstance.get(`/v1/contracts/${contractId}/history/`);
  return response.data;
};

export const verifyContract = async (contractId: string, notes: string) => {
  const response = await AxiosInstance.post(`/v1/contracts/${contractId}/verify/`, { notes });
  return response.data;
};

export const removeClauseFromContract = async (
  contractId: string,
  clauseId: string,
  reason: string,
) => {
  const response = await AxiosInstance.post(
    `/v1/contracts/${contractId}/clauses/${clauseId}/remove/`,
    { reason },
  );
  return response.data;
};

// ── MB-371: versiones del contrato ──
// Las tres rutas llevan slash final. Este archivo mezcla ambas convenciones (`/preview` no lo
// lleva, `/history/` sí) porque cada endpoint la hereda del router que lo declara, así que el
// slash no se deduce mirando al vecino: éstas se verificaron contra el backend una por una. Sin
// él, Django responde 301 a la URL con slash y Axios reemite la petición — lo que en un POST
// significa perder el cuerpo por el camino.

export const listVersions = async (contractId: string) => {
  const response = await AxiosInstance.get(`/v1/contracts/${contractId}/versions/`);
  return response.data;
};

/**
 * El documento completo de una versión, para traducir los ids de participante de `party_setup` a
 * los nombres que tenían EN esa versión. La lista no trae carátula y el diff tampoco: sólo este
 * endpoint la lleva, dentro de `editor_data`.
 */
export const getVersion = async (contractId: string, number: number) => {
  const response = await AxiosInstance.get(
    `/v1/contracts/${contractId}/versions/${number}/`,
  );
  return response.data;
};

/**
 * `target` es la versión mirada y `against` la base contra la que se compara. Se omite el
 * parámetro cuando no hay base explícita en vez de mandarlo vacío: el backend interpreta su
 * ausencia como "contra la inmediatamente anterior", mientras que `?against=` sería un número
 * inválido y no lo mismo.
 */
export const getVersionDiff = async (
  contractId: string,
  target: number,
  against?: number,
) => {
  const response = await AxiosInstance.get(
    `/v1/contracts/${contractId}/versions/${target}/diff/`,
    against === undefined ? undefined : { params: { against } },
  );
  return response.data;
};

/**
 * Restaurar no reescribe ni borra la versión `number`: crea una versión NUEVA con su contenido, y
 * devuelve ese contenido en `editor_data` para que el editor lo cargue con `loadContract`. Es la
 * única vía que pasa por las migraciones de esquema del editor, que no tienen equivalente en
 * Python: un snapshot guardado hace meses sólo se moderniza al entrar por ahí.
 */
export interface RestoredVersion {
  editor_data: unknown;
  restored_from: number;
  version: number;
  /** La confirmación de la revisión de la renovación tras restaurar: `null` si se borró. */
  renewal_review?: unknown;
  /** `true` cuando restaurar borró una confirmación: la revisión se pide otra vez. */
  renewal_review_reset?: boolean;
}

export const restoreVersion = async (
  contractId: string,
  number: number,
): Promise<RestoredVersion> => {
  const response = await AxiosInstance.post(
    `/v1/contracts/${contractId}/versions/${number}/restore/`,
    {},
  );
  return response.data;
};
